import React, { useState, useEffect } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { db, storage, functions } from "../firebase";
import { useCart } from "../context/CartContext";
import { doc, getDoc } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import { FiCheck, FiTruck, FiPackage, FiClock, FiXCircle, FiExternalLink, FiDownload, FiAlertTriangle } from "react-icons/fi";

const STATUS_STEPS = [
    { key: "paid",       label: "Order Confirmed",  icon: FiCheck,   color: "#28a745", desc: "We received your order and payment." },
    { key: "processing", label: "Processing",        icon: FiPackage, color: "#17a2b8", desc: "Your books are being prepared." },
    { key: "shipped",    label: "Shipped",            icon: FiTruck,   color: "#007bff", desc: "Your order is on its way!" },
    { key: "delivered",  label: "Delivered",          icon: FiCheck,   color: "#28a745", desc: "Enjoy your books!" },
];

const STATUS_ORDER = ["pending", "pending_ath_verification", "paid", "processing", "shipped", "delivered", "cancelled"];

function StatusTimeline({ currentStatus }) {
    if (currentStatus === "cancelled") {
        return (
            <div style={{ textAlign: "center", padding: "40px 20px" }}>
                <FiXCircle size={60} color="#dc3545" style={{ marginBottom: 16 }} />
                <h3 style={{ color: "#dc3545" }}>Order Cancelled</h3>
                <p style={{ color: "#888" }}>This order has been cancelled. Contact us if you have questions.</p>
            </div>
        );
    }

    const currentIdx = STATUS_STEPS.findIndex(s => s.key === currentStatus);
    const resolvedIdx = currentIdx === -1 ? 0 : currentIdx;

    return (
        <div style={{ padding: "20px 0" }}>
            {STATUS_STEPS.map((step, i) => {
                const done = i <= resolvedIdx;
                const active = i === resolvedIdx;
                return (
                    <div key={step.key} style={{ display: "flex", gap: 20, marginBottom: 0 }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <div style={{
                                width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                                background: done ? step.color : "#f0f0f0",
                                color: done ? "white" : "#bbb",
                                transition: "all 0.4s",
                                boxShadow: active ? `0 0 0 4px ${step.color}30` : "none",
                            }}>
                                <step.icon size={20} />
                            </div>
                            {i < STATUS_STEPS.length - 1 && (
                                <div style={{ width: 2, height: 50, background: done && i < resolvedIdx ? step.color : "#f0f0f0", transition: "background 0.4s" }} />
                            )}
                        </div>
                        <div style={{ paddingTop: 10, paddingBottom: 32 }}>
                            <p style={{ fontWeight: 700, margin: 0, color: done ? "#222" : "#bbb", fontSize: 16 }}>{step.label}</p>
                            {active && <p style={{ color: "#555", margin: "4px 0 0", fontSize: 14 }}>{step.desc}</p>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ── Digital download section ─────────────────────────────────
function DigitalDownloads({ items, downloadExpiresAt }) {
    const [resolvedDigitalItems, setResolvedDigitalItems] = useState([]);
    const [downloadUrls, setDownloadUrls] = useState({});
    const [loadingUrls, setLoadingUrls] = useState(true);

    const now = Date.now();
    const expired = downloadExpiresAt && now > downloadExpiresAt;
    const expiresIn = downloadExpiresAt ? downloadExpiresAt - now : null;

    // Format time remaining
    function formatTimeLeft(ms) {
        if (ms <= 0) return "Expired";
        const h = Math.floor(ms / 3600000);
        const m = Math.floor((ms % 3600000) / 60000);
        if (h > 0) return `${h}h ${m}m remaining`;
        return `${m} minutes remaining`;
    }

    useEffect(() => {
        if (expired || !items || items.length === 0) {
            setLoadingUrls(false);
            return;
        }

        async function resolveAndFetchUrls() {
            const resolved = [];
            const urls = {};

            for (const item of items) {
                // Case 1: Direct digital item
                if (item.isDigital) {
                    let path = item.epubStoragePath;
                    if (!path) {
                        // Fallback: fetch from database
                        try {
                            const bookSnap = await getDoc(doc(db, "products", item.productId || item.id));
                            if (bookSnap.exists()) {
                                const bookData = bookSnap.data();
                                const ebookVariant = (bookData.variants || []).find(v => v.format === "eBook");
                                path = ebookVariant?.epubStoragePath || bookData.epubStoragePath || "";
                            }
                        } catch (err) {
                            console.error("Error fetching direct product fallback:", err);
                        }
                    }
                    if (path) {
                        resolved.push({
                            ...item,
                            epubStoragePath: path,
                            otdToken: item.otdToken || null
                        });
                    }
                }

                // Case 2: Bundle item
                if (item.bundleItems && (item.selectedFormat === "Bundle" || item.format === "Bundle")) {
                    for (const b of item.bundleItems) {
                        let path = b.epubStoragePath;
                        let isDig = b.isDigital;

                        // Fallback: fetch from database if missing path or digital flag
                        if (!path || !isDig) {
                            try {
                                const bookSnap = await getDoc(doc(db, "products", b.id));
                                if (bookSnap.exists()) {
                                    const bookData = bookSnap.data();
                                    const ebookVariant = (bookData.variants || []).find(v => v.format === "eBook");
                                    path = ebookVariant?.epubStoragePath || bookData.epubStoragePath || "";
                                    isDig = ebookVariant ? true : (bookData.isDigital || false);
                                }
                            } catch (err) {
                                console.error("Error fetching bundle product fallback:", err);
                            }
                        }

                        if (isDig && path) {
                            resolved.push({
                                ...b,
                                title: `${b.title} (from ${item.title})`,
                                epubStoragePath: path,
                                isDigital: true,
                                otdToken: b.otdToken || null
                            });
                        }
                    }
                }
            }

            // Now fetch storage download URLs
            for (const item of resolved) {
                if (item.otdToken) {
                    urls[item.id] = `/download-otd/${item.otdToken}`;
                } else {
                    try {
                        const url = await getDownloadURL(ref(storage, item.epubStoragePath));
                        urls[item.id] = url;
                    } catch (e) {
                        console.error("Could not get download URL for", item.title, e);
                    }
                }
            }

            setResolvedDigitalItems(resolved);
            setDownloadUrls(urls);
            setLoadingUrls(false);
        }

        resolveAndFetchUrls();
    }, [items, expired]);

    if (loadingUrls) {
        return (
            <div style={{ background: "white", borderRadius: 20, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f0ede8", marginBottom: 24 }}>
                <h3 style={{ marginBottom: 8, color: "#1565c0", display: "flex", alignItems: "center", gap: 10 }}>
                    <FiDownload size={20} /> Your Digital Downloads
                </h3>
                <p style={{ color: "#888", fontSize: 14 }}>Preparing your download links…</p>
            </div>
        );
    }

    if (resolvedDigitalItems.length === 0) return null;

    return (
        <div style={{ background: "white", borderRadius: 20, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f0ede8", marginBottom: 24 }}>
            <h3 style={{ marginBottom: 8, color: "#1565c0", display: "flex", alignItems: "center", gap: 10 }}>
                <FiDownload size={20} /> Your Digital Downloads
            </h3>

            {/* Expiry indicator */}
            {!expired && downloadExpiresAt && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#e8f5e9", border: "1px solid #a5d6a7", borderRadius: 10, padding: "10px 16px", marginBottom: 20 }}>
                    <FiClock size={16} style={{ color: "#2e7d32", flexShrink: 0 }} />
                    <p style={{ margin: 0, fontSize: 14, color: "#2e7d32", fontWeight: 600 }}>
                        Download link active — {formatTimeLeft(expiresIn)}
                    </p>
                </div>
            )}

            {expired && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: "#fff3e0", border: "1px solid #ffcc02", borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
                    <FiAlertTriangle size={18} style={{ color: "#f57c00", flexShrink: 0, marginTop: 2 }} />
                    <div>
                        <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#e65100", fontSize: 14 }}>Download link has expired</p>
                        <p style={{ margin: 0, fontSize: 13, color: "#555" }}>
                            Your 12-hour download window has passed. Please{" "}
                            <a href="/about#contact" style={{ color: "var(--accent)", fontWeight: 700 }}>contact support</a>
                            {" "}with your order number <strong>#{window.location.pathname.split("/").pop()?.slice(0, 8).toUpperCase()}</strong> to request a new link.
                        </p>
                    </div>
                </div>
            )}

            {/* 48-hour disclaimer */}
            {!expired && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#fff8e1", border: "1px solid #ffe082", borderRadius: 10, padding: "12px 16px", marginBottom: 20 }}>
                    <FiAlertTriangle size={16} style={{ color: "#f9a825", flexShrink: 0, marginTop: 1 }} />
                    <p style={{ margin: 0, fontSize: 13, color: "#5d4037", lineHeight: 1.5 }}>
                        <strong>⏱ Important:</strong> Your download link is valid for <strong>12 hours</strong> from the time of purchase.
                        After it expires, please <a href="/about#contact" style={{ color: "var(--accent)", fontWeight: 700 }}>contact support</a> with your order ID to request a new link.
                        We recommend downloading your book right away and saving it to your device.
                    </p>
                </div>
            )}

            {/* Download buttons */}
            {!expired && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {resolvedDigitalItems.map((item, i) => {
                        const url = downloadUrls[item.id];
                        return (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: url ? "#f0f7ff" : "#f9f9f9", border: `1px solid ${url ? "#90caf9" : "#eee"}`, borderRadius: 12 }}>
                                {item.imageUrl && (
                                    <img src={item.imageUrl} alt={item.title} style={{ width: 44, height: 56, objectFit: "cover", borderRadius: 6 }} />
                                )}
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontWeight: 700, margin: 0, fontSize: 15 }}>{item.title}</p>
                                    <p style={{ fontSize: 12, color: "#888", margin: "2px 0 0" }}>
                                        {item.epubStoragePath?.split(".").pop()?.toUpperCase() || "EPUB"} • Digital Edition
                                    </p>
                                </div>
                                {url ? (
                                    url.startsWith("/") ? (
                                        <Link
                                            to={url}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 8,
                                                background: "#1565c0", color: "#fff", padding: "10px 20px",
                                                borderRadius: 10, fontWeight: 700, fontSize: 14,
                                                textDecoration: "none", whiteSpace: "nowrap"
                                            }}
                                        >
                                            <FiDownload size={16} /> Download
                                        </Link>
                                    ) : (
                                        <a
                                            href={url}
                                            download
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 8,
                                                background: "#1565c0", color: "#fff", padding: "10px 20px",
                                                borderRadius: 10, fontWeight: 700, fontSize: 14,
                                                textDecoration: "none", whiteSpace: "nowrap"
                                            }}
                                        >
                                            <FiDownload size={16} /> Download
                                        </a>
                                    )
                                ) : (
                                    <span style={{ fontSize: 13, color: "#e57373" }}>File unavailable</span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function OrderTracking() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { clearCart } = useCart();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (searchParams.get("stripe_success") === "true") {
            clearCart();
        }
    }, [searchParams, clearCart]);

    useEffect(() => {
        async function load() {
            try {
                const snap = await getDoc(doc(db, "orders", id));
                if (snap.exists()) {
                    let orderData = snap.data();
                    if (orderData.status === "pending_payment" && searchParams.get("stripe_success") === "true") {
                        try {
                            const verifyPayment = httpsCallable(functions, "verifyStripePayment");
                            const verifyResult = await verifyPayment({ orderId: id });
                            if (verifyResult.data?.status === "paid") {
                                const updatedSnap = await getDoc(doc(db, "orders", id));
                                if (updatedSnap.exists()) {
                                    orderData = updatedSnap.data();
                                }
                            }
                        } catch (err) {
                            console.error("Error verifying payment securely via Cloud Function:", err);
                        }
                    }
                    setOrder({ id: snap.id, ...orderData });
                }
            } catch (e) {
                console.error("Error loading order:", e);
            }
            setLoading(false);
        }
        load();
    }, [id, searchParams]);

    useEffect(() => {
        if (!order) return;
        const isPaid = ["paid", "processing", "shipped", "delivered"].includes(order.status);
        if (isPaid && window.fbq) {
            const trackingKey = `meta_purchase_tracked_${order.id}`;
            if (!sessionStorage.getItem(trackingKey)) {
                window.fbq('track', 'Purchase', {
                    value: order.total || 0,
                    currency: 'USD',
                    content_type: 'product',
                    contents: (order.items || []).map(item => ({
                        id: item.metaContentId || item.id,
                        quantity: item.quantity
                    }))
                }, { eventID: order.id });
                sessionStorage.setItem(trackingKey, 'true');
            }
        }
    }, [order]);

    if (loading) return (
        <div className="container" style={{ padding: 100, textAlign: "center", color: "#888" }}>
            <FiClock size={40} style={{ marginBottom: 16 }} />
            <p>Loading your order…</p>
        </div>
    );

    if (!order) return (
        <div className="container" style={{ padding: 100, textAlign: "center" }}>
            <h2 style={{ color: "#dc3545" }}>Order Not Found</h2>
            <p style={{ color: "#888" }}>We couldn't find this order. Double-check your confirmation email.</p>
            <Link to="/" style={{ color: "var(--accent)", fontWeight: 700 }}>Return to Home</Link>
        </div>
    );

    const subtotal = order.subtotal ?? (order.items || []).reduce((s, i) => s + i.price * i.quantity, 0);
    // Combo bundles have physical copies but still include digital books
    const hasDigital = (order.items || []).some(i =>
        (i.isDigital && i.epubStoragePath) || (i.bundleDelivery === "Combo" && (i.bundleItems || []).length > 0)
    );
    const hasPhysical = (order.items || []).some(i => !i.isDigital);

    return (
        <div className="container" style={{ padding: "80px 20px 100px", maxWidth: 960 }}>
            {/* Header */}
            <div style={{ textAlign: "center", marginBottom: 60 }}>
                <div style={{ width: 80, height: 80, background: "#d4edda", borderRadius: "50%", margin: "0 auto 20px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FiCheck size={40} color="#28a745" />
                </div>
                <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "2.5rem", marginBottom: 8 }}>
                    {order.status === "pending_ath_verification" 
                        ? "Awaiting Payment Verification"
                        : ["paid","processing","shipped","delivered"].includes(order.status) 
                            ? "Thank You For Your Order!" 
                            : "Order Status"
                    }
                </h1>
                <p style={{ color: "#888" }}>Order <strong>#{order.id.slice(0, 8).toUpperCase()}</strong> — {order.email}</p>
                {order.status === "pending_ath_verification" && (
                    <div style={{ background: "#fff5eb", padding: "12px 24px", borderRadius: 12, border: "1px solid #ffe8cc", display: "inline-block", marginTop: 10 }}>
                        <p style={{ margin: 0, color: "#d9480f", fontSize: 14, fontWeight: 600 }}>
                            We are verifying your ATH Móvil transfer. Your order will be processed once confirmed.
                        </p>
                    </div>
                )}
            </div>

            <div style={{ display: "flex", gap: 40, flexWrap: "wrap", alignItems: "flex-start" }}>
                {/* Left column: Timeline */}
                <div style={{ flex: "1 1 300px" }}>
                    <div style={{ background: "white", borderRadius: 20, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f0ede8" }}>
                        <h3 style={{ marginBottom: 24, color: "var(--green-primary)" }}>
                            {hasDigital && !hasPhysical ? "📧 Digital Order" : "Order Progress"}
                        </h3>

                        {hasDigital && !hasPhysical ? (
                            /* Digital-only: simplified status */
                            <div style={{ textAlign: "center", padding: "20px 0" }}>
                                <div style={{ width: 64, height: 64, background: "#d4edda", borderRadius: "50%", margin: "0 auto 16px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <FiCheck size={32} color="#28a745" />
                                </div>
                                <p style={{ fontWeight: 700, color: "#28a745", fontSize: 18 }}>Payment Confirmed</p>
                                <p style={{ color: "#888", fontSize: 14 }}>Your digital book is ready to download below.</p>
                            </div>
                        ) : (
                            <StatusTimeline currentStatus={order.status} />
                        )}

                        {/* Multiple Shipments */}
                        {order.shipments && order.shipments.length > 0 && (
                            <div style={{ marginTop: 20 }}>
                                <h4 style={{ fontSize: 14, fontWeight: 700, color: "#555", textTransform: "uppercase", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
                                    <FiPackage size={16} /> Your Shipments ({order.shipments.length})
                                </h4>
                                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                                    {order.shipments.map((ship, idx) => (
                                        <div key={idx} style={{ background: "#f8f9ff", border: "1px solid #e1e7ff", borderRadius: 16, padding: 20 }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                    <div style={{ width: 36, height: 36, background: "#e1e7ff", color: "#3f51b5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <FiTruck size={18} />
                                                    </div>
                                                    <div>
                                                        <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#333" }}>{ship.trackingNumber}</p>
                                                        <p style={{ margin: 0, fontSize: 11, color: "#888" }}>Shipped on {new Date(ship.shippedAt).toLocaleDateString()}</p>
                                                    </div>
                                                </div>
                                                <a
                                                    href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${ship.trackingNumber}`}
                                                    target="_blank" rel="noreferrer"
                                                    style={{ background: "#3f51b5", color: "white", padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700, textDecoration: "none", display: "flex", alignItems: "center", gap: 6 }}
                                                >
                                                    Track <FiExternalLink size={12} />
                                                </a>
                                            </div>
                                            
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                                                {ship.items.map((si, sidx) => (
                                                    <div key={sidx} style={{ 
                                                        display: "flex", alignItems: "center", gap: 8, background: "white", padding: "6px 10px", borderRadius: 10, border: "1px solid #eee", fontSize: 12 
                                                    }}>
                                                        {si.imageUrl && <img src={si.imageUrl} alt="" style={{ width: 20, height: 28, objectFit: "cover", borderRadius: 3 }} />}
                                                        <span style={{ fontWeight: 600 }}>{si.title}</span>
                                                        <span style={{ color: "#888" }}>x{si.quantity}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Legacy Single Tracking fallback */}
                        {!order.shipments && order.trackingNumber && (
                            <div style={{ background: "#e3f2fd", borderRadius: 12, padding: 20, marginTop: 8 }}>
                                <p style={{ fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 8, color: "#1565c0" }}>
                                    <FiTruck size={18} /> Tracking Number
                                </p>
                                <p style={{ fontFamily: "monospace", fontSize: 16, fontWeight: 700, margin: "0 0 10px", letterSpacing: 1 }}>
                                    {order.trackingNumber}
                                </p>
                                <a
                                    href={`https://tools.usps.com/go/TrackConfirmAction?tLabels=${order.trackingNumber}`}
                                    target="_blank" rel="noreferrer"
                                    style={{ color: "#1565c0", fontWeight: 600, fontSize: 14, display: "flex", alignItems: "center", gap: 6 }}
                                >
                                    Track on USPS <FiExternalLink size={14} />
                                </a>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right column: Downloads + Details */}
                <div style={{ flex: "1 1 400px" }}>
                    {/* Digital downloads card */}
                    {hasDigital && ["paid","processing","shipped","delivered"].includes(order.status) && (
                        <DigitalDownloads
                            items={order.items}
                            downloadExpiresAt={order.downloadExpiresAt}
                        />
                    )}

                    {hasDigital && order.status === "pending_ath_verification" && (
                        <div style={{ background: "white", borderRadius: 20, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f0ede8", marginBottom: 24 }}>
                            <h3 style={{ marginBottom: 12, color: "#d9480f", display: "flex", alignItems: "center", gap: 10 }}>
                                <FiDownload size={20} /> Digital Downloads
                            </h3>
                            <p style={{ color: "#666", fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                                Your digital content will be available for download here <strong>immediately after</strong> we verify your ATH Móvil transfer.
                            </p>
                        </div>
                    )}

                    {/* Delivery / shipping info */}
                    <div style={{ background: "white", borderRadius: 20, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f0ede8", marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 20, color: "var(--green-primary)" }}>
                            {hasDigital && !hasPhysical ? "Billing Information" : "Delivery Information"}
                        </h3>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
                            <div>
                                <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#aaa", marginBottom: 6 }}>Customer</p>
                                <p style={{ fontWeight: 600, margin: 0 }}>{order.customerName}</p>
                                <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{order.email}</p>
                                <p style={{ color: "#666", fontSize: 13, margin: 0 }}>{order.phone}</p>
                            </div>
                            {hasPhysical && (
                                <div>
                                    <p style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: "#aaa", marginBottom: 6 }}>Ship To</p>
                                    {order.shippingLine1 ? (
                                        <p style={{ fontSize: 14, lineHeight: 1.6, margin: 0 }}>
                                            {order.shippingLine1}<br />
                                            {order.shippingCity}, {order.shippingState} {order.shippingZip}<br />
                                            {order.shippingCountry}
                                        </p>
                                    ) : <p style={{ color: "#888", fontSize: 13, margin: 0 }}>{order.shippingMethod || "Local Pickup"}</p>}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Items & Total */}
                    <div style={{ background: "white", borderRadius: 20, padding: 32, boxShadow: "0 4px 20px rgba(0,0,0,0.06)", border: "1px solid #f0ede8" }}>
                        <h3 style={{ marginBottom: 20, color: "var(--green-primary)" }}>Items Ordered</h3>
                        {(order.items || []).map((item, i) => (
                            <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "10px 0", borderBottom: "1px solid #f5f5f5" }}>
                                {item.imageUrl && <img src={item.imageUrl} alt={item.title} style={{ width: 44, height: 56, objectFit: "cover", borderRadius: 6 }} />}
                                <div style={{ flex: 1 }}>
                                    <p style={{ fontWeight: 600, margin: 0 }}>
                                        {item.title}
                                        {item.isDigital && (
                                            <span style={{ marginLeft: 8, fontSize: 11, background: "#e3f2fd", color: "#1565c0", padding: "2px 8px", borderRadius: 10, fontWeight: 700 }}>
                                                Digital
                                            </span>
                                        )}
                                    </p>
                                    <p style={{ fontSize: 13, color: "#888", margin: 0 }}>Qty: {item.quantity}</p>
                                </div>
                                <strong>${(item.price * item.quantity).toFixed(2)}</strong>
                            </div>
                        ))}
                        <div style={{ marginTop: 20, textAlign: "right" }}>
                            <p style={{ color: "#888", margin: "0 0 6px", fontSize: 14 }}>Subtotal: ${subtotal.toFixed(2)}</p>
                            {hasPhysical && (
                                <p style={{ color: "#888", margin: "0 0 10px", fontSize: 14 }}>
                                    Shipping: {order.shippingCost === 0 ? "Free" : `$${(order.shippingCost || 0).toFixed(2)}`}
                                    {order.shippingMethod ? ` (${order.shippingMethod})` : ""}
                                </p>
                            )}
                            <p style={{ fontWeight: 700, fontSize: 20, color: "var(--green-primary)", margin: 0 }}>
                                Total: ${(order.total || 0).toFixed(2)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div style={{ textAlign: "center", marginTop: 48 }}>
                <Link to="/shop" style={{ color: "var(--accent)", fontWeight: 700, fontSize: 16, textDecoration: "none" }}>
                    ← Continue Shopping
                </Link>
            </div>
        </div>
    );
}
